import type { TFunction } from 'i18next';
import { buildCsv } from 'utils/csv';
import { availabilityLabel } from 'utils/unavailability';
import type { TechnicianListItem } from 'api/techniciansApi';

/**
 * The CSV export the mock-up drew without a handler.
 *
 * `technicianFullName` lives here rather than beside the columns on purpose: the file and the
 * screen must spell a name the same way, and two implementations would drift.
 */

export const technicianFullName = (technician: TechnicianListItem): string =>
  [technician.profile.firstName, technician.profile.lastName].filter(Boolean).join(' ').trim() ||
  technician.username;

/**
 * The visible columns in screen order. The two deferred ones are absent from the file: a column
 * of empty cells would read as "no interventions", which is not what is known.
 */
export const buildTechniciansCsv = (t: TFunction, technicians: TechnicianListItem[]): string => {
  const header = [
    t('Technician'),
    t('Email'),
    t('Phone'),
    t('Company'),
    t('Country'),
    t('Areas covered'),
    t('Specialties'),
    t('Expertises'),
    t('Average rating'),
    t('Ratings received'),
    t('Availability'),
    t('Status'),
  ];

  const rows = technicians.map((technician) => [
    technicianFullName(technician),
    technician.email,
    technician.profile.phone ?? '',
    technician.profile.company ?? '',
    technician.profile.countryName ?? '',
    // Every value, never a "+38": a spreadsheet is where the whole list is wanted.
    technician.geoAreas.map((area) => area.code).join(', '),
    technician.specialties.map((specialty) => specialty.name).join(', '),
    technician.expertises.map((expertise) => expertise.name).join(', '),
    // Empty, not 0: nothing was rated.
    technician.ratings.avg == null ? '' : String(technician.ratings.avg),
    String(technician.ratings.count),
    // The word, not the periods: a spreadsheet cell holding three date ranges is unreadable, and
    // the record is where a period is looked at.
    availabilityLabel(t, technician.availability),
    technician.isActive ? t('Active') : t('Inactive'),
  ]);

  return buildCsv(header, rows);
};
